import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiService, { NotFoundError } from './services/ApiService';
import AdminKeyService from './services/AdminKeyService';
import AuthorizationService from './services/AuthorizationService';
import UrlService from './services/UrlService';
import DecryptionError from './services/DecryptionError';
import NotFoundScreen from './components/NotFoundScreen';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';
import { useShare } from './hooks/useShare';

function RankedElectionVoteDetail() {
    const { t } = useTranslation(['voting', 'common', 'election']);
    const [rankedElectionVote, setRankedElectionVote] = useState(null);
    const [electionInfo, setElectionInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const [notFoundType, setNotFoundType] = useState('vote');
    const { electionId, voteId } = useParams();
    const location = useLocation();
    const [shareUrls, setShareUrls] = useState({ normal: '', admin: '' });
    const { share, copySuccess } = useShare();

    // Page metadata
    const { title, description } = usePageMetadata(electionInfo);
    const [copyPreferencesSuccess, setCopyPreferencesSuccess] = useState('');

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, electionId, voteId);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, electionId);

        setLoading(true);
        
        // Fetch both election info and vote details
        Promise.all([
            ApiService.getElection(electionId),
            ApiService.getVote(electionId, voteId)
        ])
        .then(([electionData, voteData]) => {
            setElectionInfo(electionData);
            setRankedElectionVote(voteData);
            setLoading(false);
            setError(null);
            setIsDecryptionError(false);

            // Sync URL with correct encryption params from localStorage
            UrlService.syncEncryptionKeysWithUrl(electionId);

            // Generate share URLs
            const normalUrl = UrlService.generateVoteShareUrl(electionId, voteId, false, electionData.isEncrypted);
            const adminUrl = UrlService.generateVoteShareUrl(electionId, voteId, true, electionData.isEncrypted);
            setShareUrls({ normal: normalUrl, admin: adminUrl });
        })
        .catch(error => {
            console.error('Error fetching vote details:', error);
            if (error instanceof NotFoundError) {
                setIsNotFoundError(true);
                setNotFoundType(error.resourceType || 'vote');
            } else if (error instanceof DecryptionError) {
                setError(t('common:errors.decryptionFailed'));
                setIsDecryptionError(true);
            } else {
                setError(t('voting:errors.voteLoadFailed'));
                setIsDecryptionError(false);
            }
            setLoading(false);
        });
    }, [electionId, voteId, location]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopyVoteLink = async () => {
        // Build formatted text with title and description
        const titleLine = electionInfo?.description
            ? `${electionInfo.title} - ${electionInfo.description}`
            : electionInfo?.title || t('voting:detail.voteTitle');

        const formattedText = `${t('voting:shareText')}\n${titleLine}`;

        await share({
            url: shareUrls.normal,
            title: electionInfo?.title || t('voting:detail.voteTitle'),
            text: formattedText,
            successMessage: t('voting:states.voteLinkCopied'),
            errorMessage: t('voting:states.linkCopyFailed')
        });
    };

    const handleCopyAdminLink = async () => {
        // Build formatted text with title and description
        const titleLine = electionInfo?.description
            ? `${electionInfo.title} - ${electionInfo.description}`
            : electionInfo?.title || t('voting:detail.voteTitle');

        const formattedText = `${t('voting:shareAdminText')}\n${titleLine}`;

        await share({
            url: shareUrls.admin,
            title: electionInfo?.title || t('voting:detail.voteTitle'),
            text: formattedText,
            successMessage: t('voting:states.adminLinkCopied'),
            errorMessage: t('voting:states.linkCopyFailed')
        });
    };

    const generatePreferencesText = (rankings, t) => {
        if (!rankings || rankings.length === 0) {
            return '';
        }
        
        return rankings.map((ranking, index) => {
            const position = index + 1;
            return `${position}. ${ranking.name}`;
        }).join('\n');
    };

    const handleCopyPreferencesText = async () => {
        try {
            const preferencesText = generatePreferencesText(rankings, t);
            if (!preferencesText) {
                setCopyPreferencesSuccess(t('voting:states.textCopyFailed'));
                setTimeout(() => setCopyPreferencesSuccess(''), 3000);
                return;
            }
            
            await navigator.clipboard.writeText(preferencesText);
            setCopyPreferencesSuccess(t('voting:states.preferencesTextCopied'));
            setTimeout(() => setCopyPreferencesSuccess(''), 3000);
        } catch (err) {
            console.error('Failed to copy preferences text:', err);
            setCopyPreferencesSuccess(t('voting:states.textCopyFailed'));
            setTimeout(() => setCopyPreferencesSuccess(''), 3000);
        }
    };

    const getVoteRankings = () => {
        if (!rankedElectionVote?.options) {
            return { rankedOptions: [], vetoedOptions: [] };
        }

        // Sort vote options by rank 
        const sortedVoteOptions = [...rankedElectionVote.options].sort((a, b) => a.rank - b.rank);
        
        const rankedOptions = [];
        const vetoedOptions = [];
        
        sortedVoteOptions.forEach(voteOption => {
            const option = {
                rank: voteOption.rank,
                name: voteOption.name || t('voting:list.preview.unknownOption'),
                id: voteOption.id
            };
            
            if (voteOption.isVetoed) {
                vetoedOptions.push(option);
            } else {
                rankedOptions.push(option);
            }
        });
        
        return { rankedOptions, vetoedOptions };
    };

    const { rankedOptions, vetoedOptions } = getVoteRankings();
    const rankings = rankedOptions; // For backward compatibility

    if (loading) {
        return (
            <div className="vote-detail-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('voting:states.loading')}</span>
                            </div>
                            <p className="text-light mt-3">{t('voting:detail.loadingDetails')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election or vote was not found
    if (isNotFoundError) {
        return <NotFoundScreen type={notFoundType} />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="vote-detail-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-detail-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="vote-detail-container">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-detail-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                                <div className="vote-detail-nav">
                                    <Link to="/" className="btn btn-outline-secondary me-2">
                                        {t('voting:navigation.backToHome')}
                                    </Link>
                                    <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}`, electionId)} className="btn btn-outline-primary">
                                        {t('voting:navigation.backToElection')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className="vote-detail-container">
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="vote-detail-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm me-2">
                                {t('voting:navigation.backToHome')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}`, electionId)} className="btn btn-outline-light btn-sm me-2">
                                {t('voting:navigation.electionDetail')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes`, electionId)} className="btn btn-outline-light btn-sm">
                                {t('voting:navigation.allVotes')}
                            </Link>
                        </div>

                        {/* Election Context Card */}
                        <div className="vote-detail-card mb-4">
                            <div className="vote-detail-header">
                                <h1 className="vote-detail-title">{t('voting:detail.title')}</h1>
                                <div className="election-context">
                                    <h2 className="election-name">{electionInfo.title}</h2>
                                    {electionInfo.description && (
                                        <p className="election-description"><LinkText text={electionInfo.description} /></p>
                                    )}
                                </div>
                                
                                {/* Share buttons */}
                                <div className="share-buttons mt-3">
                                    <button
                                        className="btn btn-outline-primary btn-sm me-2"
                                        onClick={handleCopyVoteLink}
                                        title={t('voting:detail.shareVoteTitle')}
                                    >
                                        📋 {t('voting:detail.shareVote')}
                                    </button>
                                    {AdminKeyService.hasVoteAdminKey(voteId) && (
                                        <button
                                            className="btn btn-outline-warning btn-sm"
                                            onClick={handleCopyAdminLink}
                                            title={t('voting:detail.shareAdminTitle')}
                                        >
                                            🔑 {t('voting:detail.shareAdminLink')}
                                        </button>
                                    )}
                                </div>
                                
                                {/* Copy Success Feedback */}
                                {copySuccess && (
                                    <div className="alert alert-success mt-2 mb-0" role="alert">
                                        {copySuccess}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Actions Card */}
                        <div className="vote-detail-card mb-4">
                            <div className="quick-actions">
                                <div className="row g-3">
                                    <div className={AuthorizationService.canEditVote(electionId, voteId, electionInfo) ? "col-md-6" : "col-md-12"}>
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/result`, electionId)}
                                            className="btn btn-success btn-lg w-100 vote-action-btn"
                                        >
                                            {t('election:actions.viewElectionResult')}
                                        </Link>
                                    </div>
                                    {AuthorizationService.canEditVote(electionId, voteId, electionInfo) && (
                                        <div className="col-md-6">
                                            <Link
                                                to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}/update-vote`, electionId)}
                                                className="btn btn-warning btn-lg w-100 vote-action-btn"
                                            >
                                                ✏️ {t('voting:detail.actions.editVote')}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Vote Summary Card */}
                        <div className="vote-detail-card mb-4">
                            <div className="vote-summary-header">
                                <h3>{t('voting:detail.summary')}</h3>
                            </div>
                            <div className="vote-summary-content">
                                {rankedElectionVote?.voterName && (
                                    <div className="summary-item">
                                        <span className="summary-label">{t('voting:detail.voter')}</span>
                                        <span className="summary-value">{rankedElectionVote.voterName}</span>
                                    </div>
                                )}
                                <div className="summary-item">
                                    <span className="summary-label">{t('voting:detail.voteId')}</span>
                                    <span className="summary-value vote-id">{voteId}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">{t('voting:detail.totalPreferences')}</span>
                                    <span className="summary-value">
                                        {rankings.length > 0 && `${rankings.length} ${t('voting:detail.accepted')}`}
                                        {rankings.length > 0 && vetoedOptions.length > 0 && ', '}
                                        {vetoedOptions.length > 0 && `${vetoedOptions.length} ${t('voting:detail.rejected')}`}
                                        {rankings.length === 0 && vetoedOptions.length === 0 && `0 ${t('voting:detail.options')}`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Rankings Card */}
                        <div className="vote-detail-card mb-4">
                            <div className="rankings-header">
                                <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center align-items-md-start mb-2">
                                    <h3 className="mb-2 mb-md-0">{t('voting:detail.preferences')}</h3>
                                    {rankings.length > 0 && (
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={handleCopyPreferencesText}
                                            title={t('voting:detail.copyAsTextTitle')}
                                        >
                                            📋 {t('voting:detail.copyAsText')}
                                        </button>
                                    )}
                                </div>
                                <p className="text-muted mb-0">{t('voting:detail.description')}</p>
                            </div>
                            
                            {/* Copy Preferences Success Feedback */}
                            {copyPreferencesSuccess && (
                                <div className="alert alert-success mt-2 mb-3" role="alert">
                                    {copyPreferencesSuccess}
                                </div>
                            )}
                            
                            {rankings.length === 0 && vetoedOptions.length === 0 ? (
                                <div className="empty-rankings">
                                    <div className="empty-state-icon">🗳️</div>
                                    <p className="text-muted">{t('voting:detail.noRankings')}</p>
                                </div>
                            ) : (
                                <>
                                    {/* Ranked Options Section */}
                                    {rankings.length > 0 && (
                                        <div className="rankings-section">
                                            <h4 className="section-title mb-3">{t('voting:cast.rankedOptions')}</h4>
                                            <div className="detailed-rankings">
                                                {rankings.map((ranking, index) => (
                                                    <div key={ranking.id} className={`preference-item ${index === 0 ? 'top-choice' : ''}`}>
                                                        <div className="preference-rank">
                                                            <div className={`rank-badge ${index === 0 ? 'first-choice' : ''}`}>
                                                                {index === 0 && <span className="choice-crown">👑</span>}
                                                                #{ranking.rank}
                                                            </div>
                                                        </div>
                                                        <div className="preference-content">
                                                            <div className="preference-name"><LinkText text={ranking.name} showImagePreviews={true} /></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Vetoed Options Section */}
                                    {vetoedOptions.length > 0 && (
                                        <div className="rankings-section mt-4">
                                            <h4 className="section-title text-muted mb-3">{t('voting:cast.vetoedOptions')}</h4>
                                            <div className="detailed-rankings vetoed-choices">
                                                {vetoedOptions.map((vetoOption, index) => (
                                                    <div key={vetoOption.id} className="preference-item vetoed">
                                                        <div className="preference-rank">
                                                            <div className="rank-badge vetoed-badge">
                                                                ✗
                                                            </div>
                                                        </div>
                                                        <div className="preference-content">
                                                            <div className="preference-name"><LinkText text={vetoOption.name} showImagePreviews={true} /></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Actions Card */}
                        <div className="vote-detail-card mb-4">
                            <div className="vote-actions-header">
                                <h3>{t('voting:detail.actions.title')}</h3>
                            </div>
                            
                            <div className="vote-actions">
                                <div className="row g-3">
                                    {AuthorizationService.canEditVote(electionId, voteId, electionInfo) && (
                                        <div className="col-md-6">
                                            <Link
                                                to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}/update-vote`, electionId)}
                                                className="btn btn-warning btn-lg w-100 vote-action-btn"
                                            >
                                                ✏️ {t('voting:detail.actions.editVote')}
                                            </Link>
                                        </div>
                                    )}
                                    <div className={AuthorizationService.canEditVote(electionId, voteId, electionInfo) ? "col-md-6" : "col-md-4"}>
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes`, electionId)}
                                            className="btn btn-outline-secondary btn-lg w-100 vote-action-btn"
                                        >
                                            {t('election:actions.viewAllVotes')}
                                        </Link>
                                    </div>
                                    <div className={AuthorizationService.canEditVote(electionId, voteId, electionInfo) ? "col-md-6" : "col-md-4"}>
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/result`, electionId)}
                                            className="btn btn-success btn-lg w-100 vote-action-btn"
                                        >
                                            {t('election:actions.viewElectionResult')}
                                        </Link>
                                    </div>
                                    <div className={AuthorizationService.canEditVote(electionId, voteId, electionInfo) ? "col-md-6" : "col-md-4"}>
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/create-vote`, electionId)}
                                            className="btn btn-primary btn-lg w-100 vote-action-btn"
                                        >
                                            {t('election:actions.castYourVote')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
}


export default RankedElectionVoteDetail;
